import { Injectable } from '@angular/core';
import { Observable, of, catchError, map, shareReplay } from 'rxjs';
import { IGift } from '@app/gifts/models/gift.interface';
import { HttpClient } from '@angular/common/http';
import { forEach } from 'lodash-es';
import { IGiftCode } from '@app/gifts/models/gift-code.interface';
import { IGiftsList } from '@app/gifts/models/gifts-list.interface';

@Injectable({
  providedIn: 'root'
})
export class GiftsService {

  private cachedVisitId: string;
  private cachedGiftsList$: Observable<IGiftsList>;
  private readonly giftDefaultImagePath = 'assets/img/gift-list-item-placeholder.svg';
  private readonly initialDisplayedGiftsCount = 10;

  constructor(private http: HttpClient) { }

  getAvailableGifts(visitId: string, showAll: boolean): Observable<IGiftsList | null> {
    if (this.cachedVisitId === visitId && !showAll) {
      return this.cachedGiftsList$;
    }

    this.cachedVisitId = visitId;

    const limit = showAll ? null : this.initialDisplayedGiftsCount;
    this.cachedGiftsList$ = this.http.get<IGiftsList>(`/api/gifts/${visitId}?limit=${limit}`)
      .pipe(
        map(list => ({
            allGiftsCount: list.allGiftsCount,
            gifts: this.ensureGiftDefaultImage(list.gifts)
          })),
        shareReplay(1),
        catchError(() => of(null))
      );

    return this.cachedGiftsList$;
  }

  chooseGift(visitId: string, chosenGiftId: string): Observable<any> {
    return this.http.post(`/api/gifts/${visitId}`, { chosenGiftId });
  }

  getGiftCode(giftCodeToken: string): Observable<IGiftCode> {
    return this.http.get<IGiftCode>(`api/gifts/giftcode/${giftCodeToken}`);
  }

  getGiftReservationDetails(giftCodeToken: string): Observable<IGift> {
    return this.http.get<IGift>(`api/gifts/reservationdetails/${giftCodeToken}`);
  }

  cacheGiftsOrder(gifts: IGift[], allGiftsCount: number): void {
    this.cachedGiftsList$ = of({ gifts, allGiftsCount });
  }

  clearCachedGifts(): void {
    this.cachedVisitId = '';
    this.cachedGiftsList$ = null;
  }

  private ensureGiftDefaultImage(gifts: IGift[]): IGift[] {
    return forEach(gifts, (x: IGift) => { x.logoUrl = x.logoUrl ? x.logoUrl : this.giftDefaultImagePath });
  }

}
